<template>
  <div class="plb-backdrop" @click.self="$emit('close')">
    <button class="plb-close" type="button" aria-label="Close" @click="$emit('close')">
      <i class="fa fa-times"></i>
    </button>

    <button
      v-if="photos.length > 1"
      class="plb-nav plb-nav-prev"
      type="button"
      aria-label="Previous photo"
      @click.stop="prev"
    >
      <i class="fa fa-chevron-left"></i>
    </button>

    <figure class="plb-figure" @click.stop>
      <div v-if="broken" class="plb-broken">
        <i class="fa fa-picture-o"></i>
        <p>This photo couldn't be loaded.</p>
        <button class="plb-retry" type="button" @click="retry">Try again</button>
      </div>
      <img
        v-else
        :key="current.photoId"
        :src="current.url"
        :alt="captionText"
        class="plb-img"
        @error="onImgError"
      />
      <figcaption class="plb-caption">
        <span class="plb-teeth" v-if="currentTeeth.length > 0">
          <span v-for="t in currentTeeth" :key="t" class="plb-tooth-chip">#{{ t }}</span>
        </span>
        <span class="plb-counter" v-if="photos.length > 1">
          {{ index + 1 }} of {{ photos.length }}
        </span>
      </figcaption>
    </figure>

    <button
      v-if="photos.length > 1"
      class="plb-nav plb-nav-next"
      type="button"
      aria-label="Next photo"
      @click.stop="next"
    >
      <i class="fa fa-chevron-right"></i>
    </button>
  </div>
</template>

<script>
/**
 * Enlarged single-photo popout, opened from a tooth's camera icon or from the
 * gallery. Hand-rolled backdrop + dialog to match every other modal in this
 * folder (no b-modal anywhere in TxPresenter).
 *
 * The `@error` path matters more than it looks. Photo URLs are short-lived
 * presigned S3 GETs, and this component is reachable from a patient-view tab
 * or a Present-Mode tablet that has been sitting open — so by the time someone
 * taps a photo, the URL it was handed at page load may have expired. Rather
 * than showing a broken image, we emit `refresh-urls` once and let the parent
 * re-fetch, then re-render against the new url. The `retried` flag keeps a
 * genuinely-missing object from looping.
 */
export default {
  name: 'PhotoLightbox',
  props: {
    photos: { type: Array, default: () => [] },
    startIndex: { type: Number, default: 0 },
  },
  data() {
    return {
      index: 0,
      broken: false,
      retried: false,
    };
  },
  computed: {
    current() {
      return this.photos[this.index] || {};
    },
    currentTeeth() {
      return ((this.current && this.current.teeth) || []).slice().sort((a, b) => a - b);
    },
    captionText() {
      if (this.currentTeeth.length === 0) return 'Patient photo';
      return `Patient photo, teeth ${this.currentTeeth.map((t) => `#${t}`).join(', ')}`;
    },
  },
  watch: {
    // The parent swaps in re-signed urls after `refresh-urls`; clearing the
    // broken flag is what lets the fresh <img> actually render.
    photos() {
      this.broken = false;
      if (this.index > this.photos.length - 1) this.index = Math.max(0, this.photos.length - 1);
    },
    startIndex(v) {
      this.index = this.clampIndex(v);
    },
  },
  created() {
    this.index = this.clampIndex(this.startIndex);
  },
  mounted() {
    document.addEventListener('keydown', this.onKeydown);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown);
  },
  methods: {
    clampIndex(i) {
      const n = parseInt(i, 10);
      if (!Number.isFinite(n) || n < 0) return 0;
      return Math.min(n, Math.max(0, this.photos.length - 1));
    },
    prev() {
      this.broken = false;
      this.index = (this.index - 1 + this.photos.length) % this.photos.length;
    },
    next() {
      this.broken = false;
      this.index = (this.index + 1) % this.photos.length;
    },
    onKeydown(e) {
      if (e.key === 'Escape') this.$emit('close');
      if (e.key === 'ArrowLeft' && this.photos.length > 1) this.prev();
      if (e.key === 'ArrowRight' && this.photos.length > 1) this.next();
    },
    onImgError() {
      if (!this.retried) {
        // Almost always an expired presigned URL, not a missing object.
        this.retried = true;
        this.$emit('refresh-urls');
        return;
      }
      this.broken = true;
    },
    retry() {
      this.retried = false;
      this.broken = false;
      this.$emit('refresh-urls');
    },
  },
};
</script>

<style lang="scss" scoped>
.plb-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(9, 14, 26, 0.88);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  z-index: 1200;   /* above the photo manager, which can be the thing that opened it */
  padding: 24px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
}

.plb-figure {
  margin: 0;
  max-width: min(1100px, 100%);
  max-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.plb-img {
  max-width: 100%;
  max-height: calc(100vh - 140px);
  object-fit: contain;
  border-radius: 8px;
  background: #10151f;
}

.plb-broken {
  width: min(420px, 80vw);
  padding: 40px 24px;
  border-radius: 8px;
  background: #1a212e;
  color: #cbd5e1;
  text-align: center;

  i { font-size: 32px; opacity: 0.6; }
  p { margin: 12px 0 16px; font-size: 14px; }
}

.plb-retry {
  border: 1px solid #3f4a5f;
  background: transparent;
  color: #e2e8f0;
  border-radius: 6px;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover { background: rgba(255, 255, 255, 0.06); }
}

.plb-caption {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  justify-content: center;
}

.plb-teeth {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
}

.plb-tooth-chip {
  background: rgba(23, 162, 162, 0.22);
  border: 1px solid rgba(23, 162, 162, 0.55);
  color: #7fe3e3;
  border-radius: 11px;
  padding: 2px 9px;
  font-size: 11.5px;
  font-weight: 600;
}

.plb-counter {
  font-size: 12px;
  color: #94a3b8;
}

.plb-close {
  position: absolute;
  top: 16px;
  right: 18px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-size: 17px;
  cursor: pointer;

  &:hover { background: rgba(255, 255, 255, 0.18); }
}

.plb-nav {
  flex: 0 0 auto;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  font-size: 16px;
  cursor: pointer;

  &:hover { background: rgba(255, 255, 255, 0.18); }
}

/* On a phone the side arrows eat the photo; keyboard/swipe order still works,
   and the counter tells the patient there is more than one. */
@media (max-width: 600px) {
  .plb-nav { position: absolute; bottom: 22px; }
  .plb-nav-prev { left: 24px; }
  .plb-nav-next { right: 24px; }
}
</style>
